// CONTACT (/contact) — DIRECT CHANNELS · OFFICES & MAPS · INQUIRY
// 디자인 시안 HTML을 그대로 옮긴 화면입니다. 문구·사진·구성은 시안과 같게 유지해 주세요.
// 문의 폼: siteConfig 의 inquiryFormKey(Web3Forms)가 있으면 웹에서 바로 접수, 없으면 메일 앱을 열어 내용을 채워 준다(mailto).
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CONTACT } from "../siteConfig";
import { useLightbox } from "../components/Lightbox";
import { usePageMotion } from "../motion/usePageMotion";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";

const JUMPS = [
  ["channels", "DIRECT CHANNELS"],
  ["offices", "OFFICES & MAPS"],
  ["inquiry", "INQUIRY"],
];
// ?business= 값 → 문의 폼 사업분야 (events 는 EXHIBITION)
const BUSINESS_TO_OPTION = { TRADE: "TRADE", DIGITAL: "DIGITAL", EVENTS: "EXHIBITION" };

export default function Contact() {
  const location = useLocation();
  const [active, setActive] = useState("channels");
  const [toast, setToast] = useState({ text: "", show: false });
  const toastTimer = useRef(null);
  const [kakaoIconFailed, setKakaoIconFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const categoryRef = useRef(null);
  const lightbox = useLightbox();
  const mainRef = useRef(null);

  // 등장 효과(14번)와 제목 단어별 등장
  usePageMotion(mainRef, {
    split: ["#page-title", ".hero-right h2", "#channels-title", "#offices-title", "#inquiry-title"],
    reveal: [
      ".hero .eyebrow",
      ".hero-kicker",
      ".hero-copy",
      ".hero-right",
      ".hero-bottom",
      ".channels .section-head .eyebrow",
      ".channels .section-head > p",
      ".channel",
      ".contact-facts",
      ".offices .section-head .eyebrow",
      ".offices .section-head > p",
      ".office",
      ".inquiry-intro .eyebrow",
      ".inquiry-intro > p",
      ".area-row",
      ".form-card",
    ],
  });
  const WEB_FORM = Boolean(CONTACT.inquiryFormKey); // 문의 폼 웹 접수 키가 있으면 웹 접수, 없으면 메일 앱

  const showToast = (text) => {
    setToast({ text, show: true });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 3800);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // 보조 메뉴: 스크롤 위치에 따라 현재 구역 표시
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.filter((e) => e.isIntersecting).forEach((e) => setActive(e.target.id));
      },
      { rootMargin: "-34% 0px -55% 0px", threshold: 0 }
    );
    JUMPS.forEach(([id]) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // BUSINESS '사업 문의하기'에서 넘어오면 사업분야를 미리 선택한다
  useEffect(() => {
    const b = (new URLSearchParams(location.search).get("business") || "").toUpperCase();
    const sel = categoryRef.current;
    if (!sel || !BUSINESS_TO_OPTION[b]) return;
    const opt = [...sel.options].find((o) => o.text.startsWith(BUSINESS_TO_OPTION[b]));
    if (opt) sel.value = opt.value || opt.text;
  }, [location.search]);

  // 상담 버튼: 링크가 있으면 새 창, 없고 QR 이미지가 있으면 QR 크게 보기, 둘 다 없으면 준비 중 안내
  const openChat = (url, qr, label, opener) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else if (qr) {
      lightbox.open([{ src: qr, cap: `${label} 상담 QR`, meta: `휴대폰 카메라로 찍으면 ${label} 대화가 열립니다` }], 0, opener);
    } else showToast(`${label} 상담은 준비 중입니다. 이메일로 문의해 주세요.`);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const f = e.currentTarget;
    if (sending || !f.reportValidity()) return;
    const v = Object.fromEntries(new FormData(f).entries());
    const subject = "[INBA Thailand 사업문의] " + v.category + " / " + v.company;
    if (WEB_FORM) {
      // 웹 접수: 실제로 접수됐을 때만 성공 안내를 보여 준다
      setSending(true);
      try {
        const res = await fetch(WEB3FORMS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: CONTACT.inquiryFormKey,
            subject,
            from_name: "INBA Thailand 홈페이지",
            ...(v.botcheck ? { botcheck: true } : {}), // 숨김 칸이 체크됐으면(로봇) 서비스가 거른다
            사업분야: v.category,
            이름: v.name,
            "회사·기관": v.company,
            email: v.email,
            연락처: v.phone || "미입력",
            문의내용: v.message,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
        f.reset();
        showToast("문의가 접수되었습니다. 확인 후 담당자가 연락드리겠습니다.");
      } catch (err) {
        showToast(`전송하지 못했습니다. ${CONTACT.email} 으로 직접 메일을 보내 주세요.`);
      } finally {
        setSending(false);
      }
      return;
    }
    const body = [
      "INBA Thailand 사업 문의",
      "",
      "사업분야: " + v.category,
      "이름: " + v.name,
      "회사/기관: " + v.company,
      "이메일: " + v.email,
      "연락처: " + (v.phone || "미입력"),
      "",
      "문의내용:",
      "----------------",
      v.message,
      "----------------",
    ];
    window.location.href =
      "mailto:" + CONTACT.email + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body.join("\n"));
    showToast("메일 작성 창을 열었습니다. 메일 앱에서 최종 발송해 주세요.");
  };

  return (
    <>
      <div className="subnav">
        <div className="wrap">
          <div className="crumb"><b>06</b>CONTACT</div>
          <nav className="jump" aria-label="페이지 내 메뉴">
            {JUMPS.map(([id, label]) => (
              <a key={id} className={active === id ? "active" : undefined} href={`#${id}`}>
                {label}
              </a>
            ))}
          </nav>
        </div>
      </div>
      <main ref={mainRef}>
        <section className="hero wrap" aria-labelledby="page-title">
          <div className="eyebrow">CONTACT / INBA THAILAND</div>
          <div className="hero-grid">
            <div>
              <h1 id="page-title">{"LET'S CONNECT."}<br /><span>{"LET'S GET TO WORK."}</span></h1>
              <p className="hero-kicker">한국과 태국, 현장에서 답을 찾습니다.</p>
              <p className="hero-copy">태국 시장 진출부터 유통·디지털 마케팅·전시·행사까지. 필요한 내용을 남겨주시면 INBA Thailand 담당자가 직접 확인합니다.</p>
            </div>
            <div className="hero-right">
              <div className="small-label">BUSINESS INQUIRY / GET IN TOUCH</div>
              <h2>Ideas become action.<br />Start a conversation.</h2>
              <p>국경을 넘는 비즈니스, INBA와 이야기해 보세요.</p>
              <a href="mailto:info@inbathailand.com" className="hero-email">
                info@inbathailand.com
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 19 19 5M8 5h11v11" />
                </svg>
              </a>
            </div>
          </div>
          <div className="hero-bottom">
            <span>{"SEOUL \u00a0 · \u00a0 BANGKOK / ONE EXECUTION NETWORK"}</span>
            <span><b>SCROLL TO CONNECT</b>{" \u00a0 ↓"}</span>
          </div>
        </section>
        <section className="channels" id="channels" aria-labelledby="channels-title">
          <div className="wrap">
            <div className="section-head">
              <div><div className="eyebrow">01 / DIRECT CONNECTION</div><h2 id="channels-title">Reach our team.</h2></div>
              <p>복잡한 연락처를 찾을 필요 없이, 가장 편한 방법으로 상담을 시작하세요.</p>
            </div>
            <div className="channel-grid">
              <article className="channel">
                <div className="channel-top">
                  <span className="channel-name">{"01 / EMAIL "}<span>· BUSINESS</span></span>
                  <span className="brand-chip mail">
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="1" />
                      <path d="m4 7 8 6 8-6" />
                    </svg>
                  </span>
                </div>
                <h3>이메일 문의</h3>
                <p>제안서, 견적 요청, 협업 제안 등 상세한 내용을 보내주세요.</p>
                <a className="channel-action" href="mailto:info@inbathailand.com">
                  {"EMAIL US "}
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 19 19 5M8 5h11v11" />
                  </svg>
                </a>
              </article>
              <article className="channel">
                <div className="channel-top">
                  <span className="channel-name">02 / KAKAOTALK</span>
                  <span className="brand-chip kakao">
                    {/* 외부 카카오 아이콘을 못 불러오면 아래 대체 그림을 보여 준다 */}
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/e/e3/KakaoTalk_logo.svg"
                      alt=""
                      hidden={kakaoIconFailed}
                      onError={() => setKakaoIconFailed(true)}
                    />
                    <span style={{ display: kakaoIconFailed ? "block" : "none" }} aria-hidden="true">
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="KakaoTalk">
                        {" "}
                        <rect x="0" y="0" width="64" height="64" rx="15" fill="#FEE500" />
                        <path d="M32 12c-14.1 0-25.5 8.6-25.5 19.3 0 6.8 4.6 12.8 11.7 16.2l-2.9 10.7c-.2.9.8 1.6 1.6 1.1l12.6-8.2c1.4.2 2.8.3 4.1.3 14.1 0 25.5-8.7 25.5-19.4S46.1 12 32 12Z" fill="#391B1B" />
                        <text x="32" y="36.5" fontFamily="Arial,sans-serif" fontSize="13" fontWeight="800" textAnchor="middle" fill="#FEE500">
                          TALK
                        </text>
                        {" "}
                      </svg>
                      {" "}
                    </span>
                  </span>
                </div>
                <h3>카카오톡 상담</h3>
                <p>한국어 상담을 위한 카카오톡 연결 버튼입니다.</p>
                <button
                  className="channel-action"
                  type="button"
                  id="kakaoChat"
                  onClick={(e) => openChat(CONTACT.kakaoChatUrl, CONTACT.kakaoQrImage, "카카오톡", e.currentTarget)}
                >
                  {"OPEN KAKAOTALK "}
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 19 19 5M8 5h11v11" />
                  </svg>
                </button>
              </article>
              <article className="channel">
                <div className="channel-top">
                  <span className="channel-name">03 / LINE</span>
                  <span className="brand-chip line">
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      {/*! Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free (Icons: CC BY 4.0, Fonts: SIL OFL 1.1, Code: MIT License) Copyright 2024 Fonticons, Inc. */}
                      <path d="M311 196.8v81.3c0 2.1-1.6 3.7-3.7 3.7h-13c-1.3 0-2.4-.7-3-1.5l-37.3-50.3v48.2c0 2.1-1.6 3.7-3.7 3.7h-13c-2.1 0-3.7-1.6-3.7-3.7V196.9c0-2.1 1.6-3.7 3.7-3.7h12.9c1.1 0 2.4 .6 3 1.6l37.3 50.3V196.9c0-2.1 1.6-3.7 3.7-3.7h13c2.1-.1 3.8 1.6 3.8 3.5zm-93.7-3.7h-13c-2.1 0-3.7 1.6-3.7 3.7v81.3c0 2.1 1.6 3.7 3.7 3.7h13c2.1 0 3.7-1.6 3.7-3.7V196.8c0-1.9-1.6-3.7-3.7-3.7zm-31.4 68.1H150.3V196.8c0-2.1-1.6-3.7-3.7-3.7h-13c-2.1 0-3.7 1.6-3.7 3.7v81.3c0 1 .3 1.8 1 2.5c.7 .6 1.5 1 2.5 1h52.2c2.1 0 3.7-1.6 3.7-3.7v-13c0-1.9-1.6-3.7-3.5-3.7zm193.7-68.1H327.3c-1.9 0-3.7 1.6-3.7 3.7v81.3c0 1.9 1.6 3.7 3.7 3.7h52.2c2.1 0 3.7-1.6 3.7-3.7V265c0-2.1-1.6-3.7-3.7-3.7H344V247.7h35.5c2.1 0 3.7-1.6 3.7-3.7V230.9c0-2.1-1.6-3.7-3.7-3.7H344V213.5h35.5c2.1 0 3.7-1.6 3.7-3.7v-13c-.1-1.9-1.7-3.7-3.7-3.7zM512 93.4V419.4c-.1 51.2-42.1 92.7-93.4 92.6H92.6C41.4 511.9-.1 469.8 0 418.6V92.6C.1 41.4 42.2-.1 93.4 0H419.4c51.2 .1 92.7 42.1 92.6 93.4zM441.6 233.5c0-83.4-83.7-151.3-186.4-151.3s-186.4 67.9-186.4 151.3c0 74.7 66.3 137.4 155.9 149.3c21.8 4.7 19.3 12.7 14.4 42.1c-.8 4.7-3.8 18.4 16.1 10.1s107.3-63.2 146.5-108.2c27-29.7 39.9-59.8 39.9-93.1z" />
                    </svg>
                  </span>
                </div>
                <h3>LINE 상담</h3>
                <p>태국 현지 담당자에게 LINE으로 직접 연결합니다.</p>
                {CONTACT.lineChatUrl ? (
                  <a id="lineChat" className="channel-action" href={CONTACT.lineChatUrl} target="_blank" rel="noopener noreferrer">
                    {"OPEN LINE "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 19 19 5M8 5h11v11" />
                    </svg>
                  </a>
                ) : (
                  <button
                    id="lineChat"
                    className="channel-action"
                    type="button"
                    onClick={(e) => openChat("", CONTACT.lineQrImage, "LINE", e.currentTarget)}
                  >
                    {"OPEN LINE "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 19 19 5M8 5h11v11" />
                    </svg>
                  </button>
                )}
              </article>
            </div>
            <div className="contact-facts">
              <span><b>THAILAND</b>{" \u00a0 "}<a href="tel:+66926660155">+66 92 666 0155</a></span>
              <span><b>KOREA</b>{" \u00a0 "}<a href="tel:+823180676603">+82 31 8067 6603</a></span>
              <span><b>OFFICE HOURS</b>{" \u00a0 MON–FRI 09:00–18:00 ICT"}</span>
            </div>
          </div>
        </section>
        <section id="offices" className="offices wrap" aria-labelledby="offices-title">
          <div className="section-head">
            <div><div className="eyebrow">{"02 / OFFICES & LOCATION"}</div><h2 id="offices-title">Two locations. One team.</h2></div>
            <p>한국과 태국의 거점에서 각 시장의 실무 환경에 맞춰 연결하고 실행합니다.</p>
          </div>
          <div className="offices-grid">
            <article className="office korea">
              <div className="top">
                <div><div className="office-tag">{"KOREA OFFICE "}<em>/ SUWON</em></div><h3>한국 본사</h3></div>
                <div className="office-symbol">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                </div>
              </div>
              <div className="office-map">
                <div className="map-fallback">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                  <strong>SUWON · GWANGGYO</strong>
                  <span>실제 지도를 불러오고 있습니다.</span>
                </div>
                <iframe title="INBA 한국 본사 실제 구글 지도" src={"https://maps.google.com/maps?q=%EA%B2%BD%EA%B8%B0%EB%8F%84%20%EC%88%98%EC%9B%90%EC%8B%9C%20%EC%98%81%ED%86%B5%EA%B5%AC%20%EC%B0%BD%EB%A3%A1%EB%8C%80%EB%A1%9C%20260%20%EA%B4%91%EA%B5%90%EC%84%BC%ED%8A%B8%EB%9F%B4%EB%B9%84%EC%A6%88%ED%83%80%EC%9B%8C%20510%ED%98%B8%2C%20%EB%8C%80%ED%95%9C%EB%AF%BC%EA%B5%AD&output=embed"} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
              </div>
              <div className="office-body">
                <div className="label">OFFICE ADDRESS</div>
                <address>경기도 수원시 영통구 창룡대로 260<br />광교센트럴비즈타워 510호</address>
                <div className="local">Gwanggyo Central Biz Tower 510, 260 Changnyong-daero, Suwon, Republic of Korea</div>
                <div className="office-links">
                  <a href={"https://www.google.com/maps/search/?api=1&query=%EA%B2%BD%EA%B8%B0%EB%8F%84%20%EC%88%98%EC%9B%90%EC%8B%9C%20%EC%98%81%ED%86%B5%EA%B5%AC%20%EC%B0%BD%EB%A3%A1%EB%8C%80%EB%A1%9C%20260%20%EA%B4%91%EA%B5%90%EC%84%BC%ED%8A%B8%EB%9F%B4%EB%B9%84%EC%A6%88%ED%83%80%EC%9B%8C%20510%ED%98%B8%2C%20%EB%8C%80%ED%95%9C%EB%AF%BC%EA%B5%AD"} target="_blank" rel="noopener noreferrer">
                    {"GOOGLE MAPS "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 19 19 5M8 5h11v11" />
                    </svg>
                  </a>
                  <a href="tel:+823180676603">
                    {"CALL KOREA "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.8v3a2 2 0 0 1-2.2 2c-9.1-.7-16.1-7.7-16.8-16.8A2 2 0 0 1 5 2.8h3a2 2 0 0 1 2 1.7l.4 2.3a2 2 0 0 1-.6 1.7L8.4 9.9a14.1 14.1 0 0 0 5.7 5.7l1.4-1.4a2 2 0 0 1 1.7-.6l2.3.4a2 2 0 0 1 1.7 2Z" />
                    </svg>
                  </a>
                </div>
              </div>
            </article>
            <article className="office thailand">
              <div className="top">
                <div><div className="office-tag">{"THAILAND OFFICE "}<em>/ BANGKOK</em></div><h3>방콕 지사</h3></div>
                <div className="office-symbol">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                </div>
              </div>
              <div className="office-map">
                <div className="map-fallback">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                  <strong>BANGKOK · BANG NA</strong>
                  <span>실제 지도를 불러오고 있습니다.</span>
                </div>
                <iframe title="INBA Thailand 방콕 사무실 실제 구글 지도" src={"https://maps.google.com/maps?q=25%20Phonchai%20Building%2C%202nd%20Floor%2C%20Soi%20Bangna-Trad%2034%2C%20Bang%20Na%20Tai%2C%20Bang%20Na%2C%20Bangkok%2010260%2C%20Thailand&output=embed"} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
              </div>
              <div className="office-body">
                <div className="label">OFFICE ADDRESS</div>
                <address>25 Phonchai Building (2nd Floor),<br />Soi Bangna-Trad 34, Bang Na, Bangkok 10260</address>
                <div className="local">Bangna-Trad Rd., Bang Na Tai, Bangkok, Thailand</div>
                <div className="office-links">
                  <a href={"https://www.google.com/maps/search/?api=1&query=25%20Phonchai%20Building%2C%202nd%20Floor%2C%20Soi%20Bangna-Trad%2034%2C%20Bang%20Na%20Tai%2C%20Bang%20Na%2C%20Bangkok%2010260%2C%20Thailand"} target="_blank" rel="noopener noreferrer">
                    {"GOOGLE MAPS "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 19 19 5M8 5h11v11" />
                    </svg>
                  </a>
                  <a href="tel:+66926660155">
                    {"CALL THAILAND "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.8v3a2 2 0 0 1-2.2 2c-9.1-.7-16.1-7.7-16.8-16.8A2 2 0 0 1 5 2.8h3a2 2 0 0 1 2 1.7l.4 2.3a2 2 0 0 1-.6 1.7L8.4 9.9a14.1 14.1 0 0 0 5.7 5.7l1.4-1.4a2 2 0 0 1 1.7-.6l2.3.4a2 2 0 0 1 1.7 2Z" />
                    </svg>
                  </a>
                </div>
              </div>
            </article>
          </div>
        </section>
        <section className="inquiry" id="inquiry" aria-labelledby="inquiry-title">
          <div className="wrap inquiry-grid">
            <div className="inquiry-intro">
              <div className="eyebrow">03 / PROJECT INQUIRY</div>
              <h2 id="inquiry-title">TELL US<br /><span>YOUR PLAN.</span></h2>
              <p>사업 유형과 문의 내용을 알려주시면 검토 후 연락드리겠습니다. 간단한 질문부터 협업 제안까지 가능합니다.</p>
              <div className="area-list">
                <div className="area-row"><span>01</span><div><b>TRADE</b><p>수입·인허가·유통 및 시장 진출</p></div></div>
                <div className="area-row"><span>02</span><div><b>DIGITAL</b><p>마케팅·라이브커머스·콘텐츠</p></div></div>
                <div className="area-row"><span>03</span><div><b>EXHIBITION</b><p>전시·행사·수출상담회</p></div></div>
              </div>
            </div>
            <div className="form-card">
              <div className="form-head"><b>Send an inquiry.</b><span>REQUIRED FIELD *</span></div>
              <form id="contactForm" onSubmit={onSubmit}>
                {WEB_FORM && (
                  // 스팸 방지용 숨김 칸(사람에게는 보이지 않음)
                  <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: "none" }} />
                )}
                <div className="fields">
                  <div className="field">
                    <label htmlFor="contactName">{"이름 "}<em>*</em></label>
                    <input id="contactName" name="name" required maxLength="80" autoComplete="name" placeholder="담당자 성함" />
                  </div>
                  <div className="field">
                    <label htmlFor="contactCompany">{"회사·기관명 "}<em>*</em></label>
                    <input id="contactCompany" name="company" required maxLength="120" autoComplete="organization" placeholder="회사 또는 기관명" />
                  </div>
                  <div className="field">
                    <label htmlFor="contactEmail">{"이메일 "}<em>*</em></label>
                    <input id="contactEmail" type="email" name="email" required maxLength="160" autoComplete="email" placeholder="name@company.com" />
                  </div>
                  <div className="field">
                    <label htmlFor="contactPhone">연락처</label>
                    <input id="contactPhone" name="phone" type="tel" maxLength="45" autoComplete="tel" placeholder="+82 / +66" />
                  </div>
                  <div className="field full">
                    <label htmlFor="contactCategory">{"사업분야 "}<em>*</em></label>
                    <select ref={categoryRef} id="contactCategory" name="category" required defaultValue="">
                      <option value="" disabled>사업분야를 선택해 주세요</option>
                      <option>TRADE — 수입·인허가·유통</option>
                      <option>DIGITAL — 마케팅·라이브커머스</option>
                      <option>EXHIBITION — 전시·행사·수출상담</option>
                    </select>
                  </div>
                  <div className="field full">
                    <label htmlFor="contactMessage">{"문의 내용 "}<em>*</em></label>
                    <textarea id="contactMessage" name="message" required maxLength="3000" placeholder="프로젝트 목적, 일정, 필요한 서비스 등을 자유롭게 작성해 주세요." />
                  </div>
                </div>
                <label className="consent">
                  <input id="contactConsent" type="checkbox" required />
                  <span>
                    {WEB_FORM
                      ? "입력한 연락처와 문의 내용을 문의 접수 및 회신 목적으로 사용하는 데 동의합니다."
                      : "입력한 연락처와 문의 내용을 이메일 전송 목적으로 사용하는 데 동의합니다. (문의 메일은 사용자 메일 앱에서 최종 확인 후 발송됩니다.)"}
                  </span>
                </label>
                <div className="submit-row">
                  <small>
                    {WEB_FORM
                      ? "보내 주신 내용은 INBA Thailand 담당자에게 바로 전달됩니다."
                      : "입력 후 누르면 기본 메일 앱에서 문의 내용이 채워진 새 메일이 열립니다."}
                  </small>
                  <button className="submit" type="submit" disabled={sending}>
                    {WEB_FORM ? (sending ? "SENDING… " : "SEND INQUIRY ") : "COMPOSE EMAIL "}
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 19 19 5M8 5h11v11" />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <div className={"toast" + (toast.show ? " show" : "")} id="toast" role="status" aria-live="polite">
        {toast.text}
      </div>
    </>
  );
}
